'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getTagImageUrl } from '@/utils/image-utils';

interface UserTagBadgeProps {
    tag: {
        tag_id: number;
        tag_nome: string;
        tag_cor_texto: string;
        tag_cor_fundo: string;
        tag_imagem?: string;
        tag_descricao?: string | null;
    };
    showPopover?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

export const UserTagBadge: React.FC<UserTagBadgeProps> = ({ tag, showPopover = true, size = 'md' }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const sizeClasses = {
        sm: 'w-6 h-6',
        md: 'w-10 h-10',
        lg: 'w-14 h-14'
    };

    const textPadding = {
        sm: 'px-1.5 py-0.5',
        md: 'px-3 py-1',
        lg: 'px-4 py-1.5'
    };

    const textSize = {
        sm: 'text-[8px]',
        md: 'text-[10px]',
        lg: 'text-[12px]'
    };

    return (
        <div 
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <motion.div
                className={`rounded font-bold uppercase flex items-center gap-1 cursor-help transition-transform ${textSize[size]} ${tag.tag_imagem ? 'p-0' : `${textPadding[size]} border shadow-sm`}`}
                whileHover={{ scale: 1.1 }}
                style={tag.tag_imagem ? {} : {
                    backgroundColor: tag.tag_cor_fundo,
                    borderColor: tag.tag_cor_fundo,
                    color: tag.tag_cor_texto || '#FFFFFF'
                }}
            >
                {tag.tag_imagem ? (
                    <img
                        src={getTagImageUrl(tag.tag_imagem) || ''}
                        alt={tag.tag_nome}
                        className={`${sizeClasses[size]} object-contain`}
                    />
                ) : (
                    tag.tag_nome
                )}
            </motion.div>

            {showPopover && (
                <AnimatePresence>
                    {isHovered && (tag.tag_descricao || tag.tag_nome) && (
                        <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 bg-[#1a1a1b] border border-white/10 rounded-xl p-3 shadow-2xl pointer-events-none"
                        >
                            <p className="text-white font-bold text-xs mb-1 uppercase tracking-wider">{tag.tag_nome}</p>
                            {tag.tag_descricao ? (
                                <p className="text-white/60 text-[10px] leading-relaxed italic">
                                    {tag.tag_descricao}
                                </p>
                            ) : (
                                <p className="text-white/40 text-[9px] leading-relaxed italic">
                                    {tag.tag_nome.toLowerCase()}.
                                </p>
                            )}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-8 border-transparent border-t-[#1a1a1b]" />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
};
